import React, { useState } from 'react';
import { CalendarClockIcons } from '../../../../icons/icons';
import { ensureValidDate } from '../../../../utils/helpers/dateUtils';
import { ChevronLeft, ChevronRight } from '../../../icons/icons';

interface CustomDatePickerProps {
  onSelectDates: (dates: Date[]) => void;
  selectedDates: Date[];
  error?: boolean;
  helperText?: string;
  onBlur?: () => void;
  calendarHighlightMode?: boolean; // enable enhancement
  availableDates?: Date[]; // dates that can be selected
  disabledDates?: Date[];
}

function isSameDay(a: Date, b: Date) {
  const normalize = (date: Date) => new Date(date.setHours(0, 0, 0, 0));
  return normalize(a).getTime() === normalize(b).getTime();
}

function isPastDay(date: Date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
}

function getDaysInMonth(year: number, month: number): Date[] {
  const result: Date[] = [];

  // Get the first day of the month
  const firstDay = new Date(year, month, 1);
  // Get the last day of the month
  const lastDay = new Date(year, month + 1, 0);

  // Add days from previous month to fill the first week
  const daysFromPrevMonth = firstDay.getDay();
  const prevMonth = new Date(year, month, 0);
  for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
    result.push(new Date(year, month - 1, prevMonth.getDate() - i));
  }

  // Add all days from current month
  for (
    let date = new Date(firstDay);
    date <= lastDay;
    date.setDate(date.getDate() + 1)
  ) {
    result.push(new Date(date));
  }

  // Add days from next month to fill the last week
  const daysFromNextMonth = 6 - lastDay.getDay();
  for (let i = 1; i <= daysFromNextMonth; i++) {
    result.push(new Date(year, month + 1, i));
  }

  return result;
}

function getDateRange(start: Date, end: Date): Date[] {
  const range: Date[] = [];
  let current = new Date(start);
  let endDate = new Date(end);
  if (current > endDate) [current, endDate] = [endDate, current]; // swap
  while (current <= endDate) {
    range.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return range;
}

export const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  onSelectDates,
  selectedDates,
  error,
  helperText,
  onBlur,
  availableDates,
  disabledDates,
  calendarHighlightMode,
}) => {
  // Ensure all selectedDates are valid Date objects
  const validSelectedDates = React.useMemo(() => {
    return selectedDates
      .map((date) => ensureValidDate(date))
      .filter((date): date is Date => date !== null);
  }, [selectedDates]);

  // Initialize currentMonth with the first selected date's month or current date
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (validSelectedDates.length > 0) {
      const firstSelectedDate = validSelectedDates[0];
      return new Date(
        firstSelectedDate.getFullYear(),
        firstSelectedDate.getMonth(),
        1,
      );
    }
    return new Date();
  });
  const [isOpen, setIsOpen] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartDate, setDragStartDate] = useState<Date | null>(null);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  // Update currentMonth when selectedDates changes and has dates
  React.useEffect(() => {
    if (
      validSelectedDates.length > 0 &&
      !validSelectedDates.some(
        (date) =>
          date.getMonth() === currentMonth.getMonth() &&
          date.getFullYear() === currentMonth.getFullYear(),
      )
    ) {
      const firstSelectedDate = validSelectedDates[0];
      setCurrentMonth(
        new Date(
          firstSelectedDate.getFullYear(),
          firstSelectedDate.getMonth(),
          1,
        ),
      );
    }
  }, [validSelectedDates, currentMonth]);

  const days = getDaysInMonth(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
  );

  const handleDateSelect = (date: Date) => {
    const exists = validSelectedDates.some((d) => isSameDay(d, date));
    const updated = exists
      ? validSelectedDates.filter((d) => !isSameDay(d, date))
      : [...validSelectedDates, date];
    onSelectDates(updated);
  };

  const formatDisplayDate = (date: Date) => {
    // Check if date is valid
    if (!date || isNaN(date.getTime())) {
      return '';
    }

    // Format as DD/MM/YYYY
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const changeMonth = (offset: number) => {
    const next = new Date(currentMonth);
    next.setMonth(currentMonth.getMonth() + offset);
    setCurrentMonth(next);
  };

  return (
    <div className='relative min-h-[320px] h-full w-full'>
      <div className='flex items-center'>
        <input
            type='text'
            readOnly
            value={
              validSelectedDates.length > 0
                ? validSelectedDates
                  .map(formatDisplayDate)
                  .filter((date) => date)
                  .join(', ')
                : ''
            }
            onClick={() => setIsOpen(!isOpen)}
            className={`bg-gray-50 border text-sm rounded-lg block w-full p-2.5 focus:ring-2 ${
              error
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'focus:ring-primary-dark focus:border-primary-dark'
            }`}
            placeholder='Select date(s)'
            onBlur={onBlur}
        />
        <button
            type='button'
            onClick={() => setIsOpen(!isOpen)}
            className='ml-2 text-primary-main'
        >
          <CalendarClockIcons />
        </button>
      </div>

      { helperText && error && (
        <p className='mt-1 text-sm text-red-600'>{ helperText }</p>
      ) }

      { isOpen && (
        <div className='absolute bg-white border shadow-md flex h-[270px] rounded-lg mt-5 w-full '>
          <div className='flex flex-col w-1/3 justify-center items-center p-2 bg-primary-light border-r'>
            <div className='text-lg flex flex-col text-center font-semibold'>
              <span>
                { currentMonth.toLocaleString('default', { month: 'long' }) }
              </span>
              <span>{ currentMonth.getFullYear() }</span>
            </div>

            <div className='flex mt-2 gap-6'>
              <button
                  className='rounded-full flex items-center bg-primary-background shadow-sm outline-1 w-6 h-6'
                  onClick={() => changeMonth(-1)}
              >
                <ChevronLeft className='w-4 h-4 m-auto' />
              </button>

              <button
                  className='rounded-full flex items-center bg-primary-background shadow-sm outline-1 w-6 h-6'
                  onClick={() => changeMonth(1)}
              >
                <ChevronRight className='w-4 h-4 m-auto' />
              </button>
            </div>
            { /* 👉 Clear button */ }
            { validSelectedDates.length > 0 && (
              <button
                  className='mt-4 text-sm text-red-600 underline hover:text-red-800'
                  onClick={() => onSelectDates([])}
              >
                Clear Dates
              </button>
            ) }
          </div>

          <div className='grid grid-cols-7 gap-1 p-5 w-2/3'>
            { ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className='text-start text-sm font-medium'>
                { day }
              </div>
            )) }

            { days.map((day) => {
              const isSelected = validSelectedDates.some((d) =>
                isSameDay(d, day),
              );
              const isInDragRange =
                dragStartDate && hoveredDate
                  ? getDateRange(dragStartDate, hoveredDate).some((d) =>
                    isSameDay(d, day),
                  )
                  : false;

              // Date validation and disabled state logic
              let isDisabled = false;
              let isAvailable = true;

              // Check if date is in current month
              const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

              // Disable past dates
              if (isPastDay(day)) {
                isDisabled = true;
                isAvailable = false;
              }

              // Always disable dates not in current month
              if (!isCurrentMonth) {
                isDisabled = true;
                isAvailable = false;
              }

              // Handle calendar highlight mode
              if (calendarHighlightMode && isCurrentMonth) {
                if (
                  disabledDates &&
                  disabledDates.some((d) => isSameDay(d, day))
                ) {
                  isDisabled = true;
                  isAvailable = false;
                } else if (
                  availableDates &&
                  !availableDates.some((d) => isSameDay(d, day))
                ) {
                  isAvailable = false;
                  isDisabled = true;
                }
              }

              return (
                <button
                    key={day.toISOString()}
                    disabled={isDisabled}
                    onMouseDown={(e) => {
                      if (isDisabled) return;
                      if (e.shiftKey) {
                        setIsDragging(true);
                        setDragStartDate(day);
                        setHoveredDate(day);
                      } else {
                        handleDateSelect(day);
                      }
                    }}
                    onMouseEnter={() => {
                      if (isDragging) setHoveredDate(day);
                    }}
                    onMouseUp={() => {
                      if (isDisabled) return;
                      if (isDragging && dragStartDate && hoveredDate) {
                        const range = getDateRange(dragStartDate, hoveredDate);
                        const updated = [
                          ...validSelectedDates,
                          ...range.filter(
                            (date) =>
                              !validSelectedDates.some((d) => isSameDay(d, date)),
                          ),
                        ];
                        onSelectDates(updated);
                      }
                      setIsDragging(false);
                      setDragStartDate(null);
                      setHoveredDate(null);
                    }}
                    className={`
        text-center w-8 h-8 text-sm rounded-full transition-all relative
        ${
          isDisabled
            ? 'bg-gray-50 text-gray-300 cursor-not-allowed hover:bg-gray-50 after:content-[""] after:absolute after:top-1/2 after:left-1/2 after:w-5 after:h-[1px] after:bg-gray-300 after:-translate-x-1/2 after:-translate-y-1/2 after:rotate-45'
            : ''
        }
        ${
          isSelected
            ? 'bg-primary-dark text-white font-semibold'
            : isInDragRange
              ? 'bg-primary-light text-primary-dark'
              : calendarHighlightMode && isAvailable && !isDisabled
                ? 'bg-primary-dark/10 text-gray-900'
                : ''
        }
        ${!isDisabled && !isSelected && !isInDragRange && 'hover:bg-gray-200'}
      `}
                >
                  { day.getDate() }
                </button>
              );
            }) }
          </div>
        </div>
      ) }
    </div>
  );
};
